// Basic
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Store & actions
import actionsMerchantUsers from '../../../api/actionsMerchantUsers';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useMerchantUserStore from '@/app/store/modules/merchantUser';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import Icon from '@/common-utils/components/renders/icon/Icon';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Select from '@/common-utils/components/ui/select';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
import { Button } from '@/common-utils/components/ui/button';
// Common
import ICONS from '@/common-utils/enums/icons';
import { useToast } from '@/common-utils/hooks/use-toast';
import merchantUserFields from '../../../fields/merchantUserFields';
import buildDictionary from '@/common-utils/functions/buildDictionary';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
// Lang
import { nsErrors, nsMerchantUser } from '@/app/i18n/namespaces';

const MAX_EMAIL_TO_SEND = 5;
const STEPS_NUMBER = 2;
/**
 * @param {boolean} isOpen element that triggers modal opening
 * @param {function} onClose element that triggers modal opening
 * @returns {JSX.Element} ModalSendMail Component
 */
function ModalAddMerchantUser({ isOpen, setIsOpen, merchantAccountId }) {

    // Store
    const storeRefetchMerchantUsers = useMerchantUserStore(s => s.storeRefetchMerchantUsers);
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    // State
    const [step, setStep] = useState(0);
    const [renderList, setRenderList] = useState([
        { email: '', isValid: true, profile: '', profileId: '' }
    ]);
    const [disabledAction, setDisabledAction] = useState(true);
    const [mailEntryError, setMailEntryError] = useState('');

    // Toast
    const { toast } = useToast();

    // Api Calls
    const { data: merchantUserProfile, isFetched, error: errorFetchProfiles, refetch } = actionsMerchantUsers.getMerchantUserProfiles(rootOrganization.uuid);

    // Hooks
    useEffect(() => {
        if (!isFetched && rootOrganization) {
            refetch(rootOrganization.uuid);
        }
        if (errorFetchProfiles) {
            toast({
                description: <Entity namespace={nsMerchantUser} entity='errors.fetchProfiles' />,
                variant: 'danger'
            });
        }
    }, [isFetched, errorFetchProfiles]);

    const merchantUserProfileList = merchantUserProfile ? Object.values(merchantUserProfile)
        .map((val) => val.name) : null;

    // Zod Schema
    const formSchema = z.object({
        email: z.string().email(),
        profile: z.enum(merchantUserProfileList)
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { email: '', profile: '' }
    });

    // Functions
    const handleAddMail = (index) => {
        const isEmailValid = renderList[index]?.isValid;
        const alreadyExist = renderList[index]?.alreadyExist;

        if (isEmailValid && !alreadyExist) {
            setRenderList([...renderList, { email: '', isValid: false }]);
        }
        setDisabledAction(true);
    };

    const handleRemoveMail = (e, index) => {
        e.preventDefault();
        setRenderList(renderList.filter((_, idx) => idx !== index));
        setDisabledAction(false);
        setMailEntryError('');
    };

    const getOrganizationProfileId = (profileName) => {
        if (merchantUserProfile) {
            let profileId = '';
            for (const [key, value] of Object.entries(merchantUserProfile)) {
                if (value.name === profileName) {
                    profileId = key;
                }
            }
            return profileId;
        }
    };

    const handleInputChange = (index, field, value) => {
        setRenderList((prevList) =>
            prevList.map((item, idx) => {
                if (idx === index) {
                    const newItem = { ...item, [field]: value };
                    if (field === 'email') {
                        newItem.isValid = z.string().email().safeParse(value).success;
                        newItem.alreadyExist = prevList.some(
                            (el, i) => el.email === value && i !== index
                        );
                        setMailEntryError(newItem.alreadyExist ? 'modal.add_merchant_user.already_exists' : '');
                    }
                    else if (field === 'profile') {
                        newItem.profile = value;
                        newItem.profileId = getOrganizationProfileId(value);
                    }
                    setDisabledAction(newItem.alreadyExist || !newItem.isValid || !newItem.email);
                    return newItem;
                }
                return item;
            })
        );
    };

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        storeRefetchMerchantUsers(true);
        toast({
            description: <Entity namespace={nsMerchantUser} entity='modal.add_merchant_user.success' />,
            variant: 'success'
        });
    };
    const onError = (error) => {
        const errorCode = error.response?.data?.errorCode ?? error.code ?? 'UNKNOWN_ERROR';
        setIsOpen(false);
        toast({
            description: <Entity namespace={nsErrors} entity={`api.${errorCode}`} />,
            variant: 'danger'
        });
    };

    const submitCreateMerchantUser = actionsMerchantUsers.createMerchantUser(onSuccess, onError);

    // Functions
    const validateSendingMail = () => {
        const recipientsList = renderList.map(user => ({
            email: user.email,
            profileUuid: user.profileId
        }));
        submitCreateMerchantUser.mutate({ usersList: recipientsList, merchantAccountId });
    };

    const getMerchantUserProfile = () => {
        return buildDictionary(Object.values(merchantUserProfile), 'name');
    };

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn('max-w-4xl')}>
                <HeaderTitle namespace={nsMerchantUser} title='modal.add_merchant_user.title' />
                <ModalBody className='body-send-mail-add-user'>
                    <div className='send-mail-collection'>
                        <h5>
                            <Entity
                                className={cn('text-lg')}
                                namespace={nsMerchantUser}
                                entity='modal.add_merchant_user.text'
                            />
                        </h5>
                        <div className='flex justify-center items-center pb-4'>
                            <Icon icon={ICONS.WARNING} className={cn('text-warning mr-1')} />
                            <Entity
                                className={cn('font-normal')}
                                namespace={nsMerchantUser}
                                entity='modal.add_merchant_user.info'
                            />
                        </div>
                    </div>
                    {step === 0 &&
                        <div>
                            <div className='addresses-list mt-4'>
                                <Form {...form}>
                                    <form>
                                        {renderList.map((mail, index) => (
                                            <div className='addresses-form-line' key={mail.email}>
                                                <FormField
                                                    name={`email-${index}`}
                                                    control={form.control}
                                                    render={({ field }) => (
                                                        <FormItem className='addresses-list-input'>
                                                            <FormControl>
                                                                <Input
                                                                    {...field}
                                                                    className={cn(mail.alreadyExist && 'border-red-500')}
                                                                    autoFocus
                                                                    type='email'
                                                                    value={mail.email}
                                                                    entity='modal.add_merchant_user.placeholder'
                                                                    namespace={nsMerchantUser}
                                                                    autoComplete='off'
                                                                    onChange={(e) => {
                                                                        field.onChange(e);
                                                                        handleInputChange(index, 'email', e.target.value);
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                                <FormField
                                                    name={`profile-${index}`}
                                                    control={form.control}
                                                    render={({ field }) => (
                                                        <FormItem className='select-profile'>
                                                            <FormControl>
                                                                {merchantUserProfile ?
                                                                    <Select
                                                                        {...field}
                                                                        options={getMerchantUserProfile()}
                                                                        namespace={merchantUserFields.MERCHANT_PROFILE.namespace}
                                                                        prefixEntity={merchantUserFields.MERCHANT_PROFILE.prefix}
                                                                        value={mail.profile}
                                                                        onChange={(value) => {
                                                                            handleInputChange(index, 'profile', value);
                                                                        }}
                                                                        disabled={disabledAction}
                                                                    /> :
                                                                    <Spinner className='spinner create-user-spinner' />
                                                                }
                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                                {renderList.length - 1 === index &&
                                                    renderList.length < MAX_EMAIL_TO_SEND ? (
                                                    <TooltipHelper
                                                        asChild
                                                        trigger={
                                                            <Button
                                                                onClick={() => handleAddMail(index)}
                                                                icon={ICONS.ADD}
                                                                variant='success-outline'
                                                                className={cn('ml-2 h-8 w-8 rounded-full border')}
                                                                disabled={disabledAction || !mail.profile}
                                                            />
                                                        }
                                                        content={
                                                            <Entity
                                                                entity='modal.add_merchant_user.add_address'
                                                                namespace={nsMerchantUser}
                                                            />
                                                        }
                                                    />
                                                ) : null}
                                                {index > 0 && (
                                                    <TooltipHelper
                                                        asChild
                                                        trigger={
                                                            <Button
                                                                onClick={(e) => handleRemoveMail(e, index)}
                                                                icon={ICONS.REMOVE}
                                                                variant='danger-outline'
                                                                className={cn('ml-2 h-8 w-8 rounded-full border')}
                                                            />
                                                        }
                                                        content={
                                                            <Entity
                                                                entity='modal.add_merchant_user.remove_address'
                                                                namespace={nsMerchantUser}
                                                            />
                                                        }
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </form>
                                </Form>
                            </div>
                            <Entity
                                className='max-mail-number'
                                namespace={nsMerchantUser}
                                entity='modal.add_merchant_user.maximum_mails'
                            />
                            <Entity
                                className='mail-entry-error'
                                namespace={nsMerchantUser}
                                entity={mailEntryError}
                            />
                        </div >
                    }
                    {step === 1 &&
                        <div className='confirmation'>
                            <div>
                                <div className='confirmation-list'>
                                    {
                                        renderList.length === 1 ?
                                            <Entity namespace={nsMerchantUser} entity='modal.add_merchant_user.confirmation' />
                                            :
                                            <Entity namespace={nsMerchantUser} entity='modal.add_merchant_user.confirmation_users' />
                                    }
                                </div>
                                <div>
                                    {

                                        renderList.map((element) =>
                                            <ul key={element.email}>
                                                <li>{element.email}</li>
                                            </ul>
                                        )
                                    }
                                </div>
                                <div className='confirmation-validation-text'>
                                    <Entity namespace={nsMerchantUser} entity='modal.add_merchant_user.validate' />
                                </div>
                            </div>
                        </div>
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={validateSendingMail}
                    isLoading={submitCreateMerchantUser.isLoading}
                    disableValidation={disabledAction || !renderList.every((elem) => elem.profile)}
                />
            </ModalContent>
        </Modal>
    );
}

ModalAddMerchantUser.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    merchantAccountId: PropTypes.string.isRequired
};

export default ModalAddMerchantUser;